import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { projectsApi } from '@/api/projects'
import { fieldTemplatesApi, type FieldTemplate } from '@/api/fieldTemplates'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import { ArrowLeft, ArrowRight, Check, FileText, Sparkles } from 'lucide-react'
import { TemplatePicker } from '@/components/templates/TemplatePicker'

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  system_prompt: z.string().optional(),
})

type FormData = z.infer<typeof schema>

type StepKey = 'template' | 'details'

const steps: { key: StepKey; label: string; sublabel: string; icon: typeof Sparkles }[] = [
  { key: 'template', label: 'Choose Template', sublabel: 'Pick a starting point', icon: Sparkles },
  { key: 'details', label: 'Project Details', sublabel: 'Name and context', icon: FileText },
]

export default function NewProject() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<StepKey>('template')
  const [selectedTemplate, setSelectedTemplate] = useState<FieldTemplate | null>(null)
  const [templateSelectionMade, setTemplateSelectionMade] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { register, handleSubmit, formState: { errors }, watch } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const stepIndex = steps.findIndex((s) => s.key === step)

  const onTemplateSelect = (t: FieldTemplate | null) => {
    setSelectedTemplate(t)
    setTemplateSelectionMade(true)
  }

  const onSubmit = async (data: FormData) => {
    setCreating(true)
    setError(null)
    try {
      const projectData = {
        ...data,
        system_prompt:
          data.system_prompt ||
          selectedTemplate?.project_system_prompt ||
          undefined,
      }
      const project = await projectsApi.create(projectData)

      let templateApplyError: string | null = null
      if (selectedTemplate) {
        try {
          await fieldTemplatesApi.applyToProject(project.id, selectedTemplate.id, false)
        } catch (e: any) {
          templateApplyError =
            e?.response?.data?.detail ||
            `We couldn't apply the "${selectedTemplate.name}" template to your new project. You can try applying it again from Configure Fields, or define fields manually.`
        }
      }

      queryClient.invalidateQueries({ queryKey: ['projects'] })
      navigate(`/projects/${project.id}`, {
        state: templateApplyError ? { templateApplyError } : undefined,
      })
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to create project. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  const goNext = () => {
    if (step === 'template') setStep('details')
  }
  const goBack = () => {
    if (step === 'details') setStep('template')
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button asChild variant="ghost" size="icon">
          <Link to="/projects"><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <h1 className="text-2xl font-bold">New Project</h1>
      </div>

      {/* Stepper */}
      <Stepper steps={steps} currentIndex={stepIndex} onStepClick={(i) => {
        // Only allow jumping back, not skipping forward without completing
        if (i <= stepIndex) setStep(steps[i].key)
      }} />

      {/* Step content */}
      <Card className="mt-6">
        <CardContent className="p-6">
          {step === 'template' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-blue-600" />
                  Start from a template
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Apply a pre-built field set for common document types — or start blank.
                  You can always add, edit, or remove fields later.
                </p>
              </div>

              <TemplatePicker
                selectedId={selectedTemplate?.id ?? (templateSelectionMade ? null : undefined)}
                onSelect={onTemplateSelect}
                allowSkip
              />

              <div className="flex items-center justify-between pt-4 border-t">
                <Button variant="outline" asChild>
                  <Link to="/projects">Cancel</Link>
                </Button>
                <Button onClick={goNext}>
                  {selectedTemplate ? `Continue with "${selectedTemplate.name}"` : 'Continue without template'}
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {step === 'details' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  Project details
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Name your project and provide context about the documents you'll process.
                </p>
              </div>

              {selectedTemplate && (
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Sparkles className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-blue-900">
                        Template: {selectedTemplate.name}
                      </div>
                      <div className="text-xs text-blue-700 mt-0.5">
                        {selectedTemplate.fields.length} field{selectedTemplate.fields.length !== 1 ? 's' : ''} will be added
                        {selectedTemplate.project_system_prompt && ' · template includes a project context prompt'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setStep('template')}
                      className="text-xs text-blue-700 hover:text-blue-900 underline shrink-0"
                    >
                      Change
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {error && (
                  <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="name">Project Name *</Label>
                  <Input id="name" placeholder="e.g., Contract Analysis Q4" autoFocus {...register('name')} />
                  {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input id="description" placeholder="What documents will you analyze?" {...register('description')} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="system_prompt">Project Context (optional)</Label>
                  <Textarea
                    id="system_prompt"
                    placeholder={
                      selectedTemplate?.project_system_prompt
                        ? `Leave blank to use the template's prompt:\n"${selectedTemplate.project_system_prompt}"`
                        : "Context about these documents that helps the LLM extract data more accurately..."
                    }
                    rows={4}
                    {...register('system_prompt')}
                  />
                  <p className="text-xs text-muted-foreground">
                    Prepended to every extraction call for documents in this project.
                    {selectedTemplate?.project_system_prompt && " If left empty, the template's built-in prompt is used."}
                  </p>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <Button type="button" variant="outline" onClick={goBack} disabled={creating}>
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" asChild disabled={creating}>
                      <Link to="/projects">Cancel</Link>
                    </Button>
                    <Button type="submit" disabled={creating || !watch('name')?.trim()}>
                      {creating
                        ? 'Creating...'
                        : selectedTemplate
                        ? `Create with "${selectedTemplate.name}"`
                        : 'Create Project'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}


interface StepperProps {
  steps: { key: StepKey; label: string; sublabel: string; icon: typeof Sparkles }[]
  currentIndex: number
  onStepClick: (index: number) => void
}

function Stepper({ steps, currentIndex, onStepClick }: StepperProps) {
  return (
    <nav aria-label="Progress">
      <ol className="flex items-center gap-2 sm:gap-4">
        {steps.map((s, i) => {
          const isCurrent = i === currentIndex
          const isComplete = i < currentIndex
          const isClickable = i <= currentIndex
          const Icon = s.icon
          return (
            <li key={s.key} className="flex-1 flex items-center gap-2 sm:gap-4 min-w-0">
              <button
                type="button"
                onClick={() => isClickable && onStepClick(i)}
                disabled={!isClickable}
                className={`flex items-center gap-3 min-w-0 ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <div
                  className={`relative flex items-center justify-center w-9 h-9 rounded-full border-2 shrink-0 transition-colors ${
                    isCurrent
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : isComplete
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-300 bg-white text-gray-400'
                  }`}
                >
                  {isComplete ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </div>
                <div className="hidden sm:block text-left min-w-0">
                  <div className={`text-sm font-medium ${isCurrent || isComplete ? 'text-gray-900' : 'text-gray-400'}`}>
                    Step {i + 1}
                  </div>
                  <div className={`text-xs ${isCurrent || isComplete ? 'text-gray-700' : 'text-gray-400'} truncate`}>
                    {s.label}
                  </div>
                </div>
              </button>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-px ${i < currentIndex ? 'bg-blue-600' : 'bg-gray-200'}`} />
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
